import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import axios from '../../api/axios';
import { useAuth } from '../../contexts/KeycloakContext';
import Banner from '../../ui/Banner';
import './SupportAdmin.css';

const SOCKET_URL =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5001');
const STATUS_CONFIG = {
  open: { label: 'Open', color: '#3b82f6', bg: '#eff6ff' },
  in_progress: { label: 'In Progress', color: '#f59e0b', bg: '#fffbeb' },
  resolved: { label: 'Resolved', color: '#10b981', bg: '#f0fdf4' },
  closed: { label: 'Closed', color: '#6b7280', bg: '#f9fafb' },
};

export default function SupportAdmin({ currentUser }) {
  const { keycloak } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // ── Socket.io Setup ───────────────────────────────────────────────────────
  useEffect(() => {
    const token = keycloak?.token;
    if (!token) return;

    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    socket.on('new_message', (msg) => {
      if (selectedTicket && msg.ticketId === selectedTicket.id) {
        setMessages((prev) => [...prev, msg]);
      }
      // Update unread badge on ticket list
      setTickets((prev) =>
        prev.map((t) =>
          t.id === msg.ticketId && !msg.is_admin_reply
            ? { ...t, unread_admin_count: (t.unread_admin_count || 0) + 1 }
            : t
        )
      );
    });

    socket.on('typing', ({ isTyping: typing, isAdmin: isAdminSender }) => {
      if (!isAdminSender) setOtherTyping(typing);
    });

    return () => socket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  // Join/leave ticket socket room when selection changes
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;
    if (selectedTicket) {
      socket.emit('join_ticket', selectedTicket.id);
    }
    return () => {
      if (selectedTicket) socket.emit('leave_ticket', selectedTicket.id);
    };
  }, [selectedTicket?.id]);

  // Auto-scroll to newest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Fetch Tickets ─────────────────────────────────────────────────────────
  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const res = await axios.get('/support/tickets', { params });
      if (res.data.success) setTickets(res.data.data);
    } catch (err) {
      console.error('[SupportAdmin] fetchTickets error:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // ── Open a Ticket ─────────────────────────────────────────────────────────
  const openTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setMessages([]);
    try {
      const res = await axios.get(`/support/tickets/${ticket.id}/messages`);
      if (res.data.success) {
        setMessages(res.data.data);
        // Mark as read locally
        setTickets((prev) =>
          prev.map((t) => (t.id === ticket.id ? { ...t, unread_admin_count: 0 } : t))
        );
      }
    } catch (err) {
      console.error('[SupportAdmin] openTicket error:', err);
    }
  };

  // ── Send Message ──────────────────────────────────────────────────────────
  const sendMessage = async (imageUrl = null) => {
    if (!imageUrl && (!newMessage.trim() || !selectedTicket || sending)) return;
    setSending(true);
    try {
      const res = await axios.post(`/support/tickets/${selectedTicket.id}/messages`, {
        message_text: imageUrl ? '' : newMessage.trim(),
        image_url: imageUrl,
      });
      if (res.data.success) {
        setMessages((prev) => [...prev, res.data.data]);
        if (!imageUrl) setNewMessage('');
      }
    } catch (err) {
      console.error('[SupportAdmin] sendMessage error:', err);
    } finally {
      setSending(false);
    }
  };

  // ── Image Upload ──────────────────────────────────────────────────────────
  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedTicket) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setSending(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const res = await axios.post('/support/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        await sendMessage(res.data.data.url);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to upload image';
      alert(errorMsg);
    } finally {
      setSending(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ── Update Status ─────────────────────────────────────────────────────────
  const updateStatus = async (ticketId, status) => {
    try {
      const res = await axios.patch(`/support/tickets/${ticketId}/status`, { status });
      if (res.data.success) {
        setTickets((prev) => prev.map((t) => (t.id === ticketId ? { ...t, status } : t)));
        if (selectedTicket?.id === ticketId) {
          setSelectedTicket((prev) => ({ ...prev, status }));
        }
      }
    } catch (err) {
      console.error('[SupportAdmin] updateStatus error:', err);
    }
  };

  // ── Typing Indicator ──────────────────────────────────────────────────────
  const handleTyping = (e) => {
    setNewMessage(e.target.value);
    if (!socketRef.current || !selectedTicket) return;
    if (!isTyping) {
      setIsTyping(true);
      socketRef.current.emit('typing', { ticketId: selectedTicket.id, isTyping: true });
    }
    clearTimeout(handleTyping._timeout);
    handleTyping._timeout = setTimeout(() => {
      setIsTyping(false);
      socketRef.current?.emit('typing', { ticketId: selectedTicket.id, isTyping: false });
    }, 1500);
  };

  const statusKeys = Object.keys(STATUS_CONFIG);

  return (
    <div className="sa-page">
      <Banner
        title="Support Management"
        subtitle="Manage customer support tickets and live chat in real-time."
        type="page-header"
      />

      <div className="sa-layout">
        {/* ── Ticket List Panel ─────────────────────────────────────────── */}
        <div className="sa-list-panel">
          <div className="sa-list-header">
            <h3>Tickets</h3>
            <select
              className="sa-filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              {statusKeys.map((s) => (
                <option key={s} value={s}>
                  {STATUS_CONFIG[s].label}
                </option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="sa-loading">
              <i className="fas fa-circle-notch fa-spin" /> Loading…
            </div>
          ) : tickets.length === 0 ? (
            <div className="sa-empty">
              <i className="fas fa-ticket" />
              <p>No tickets found</p>
            </div>
          ) : (
            <div className="sa-ticket-list">
              {tickets.map((t) => {
                const cfg = STATUS_CONFIG[t.status] || STATUS_CONFIG.open;
                const isSelected = selectedTicket?.id === t.id;
                return (
                  <div
                    key={t.id}
                    className={`sa-ticket-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => openTicket(t)}
                  >
                    <div className="sa-ticket-top">
                      <span className="sa-ticket-subject">
                        <span style={{ opacity: 0.6, fontSize: '11px', marginRight: '6px' }}>
                          #{t.id}
                        </span>
                        {t.subject}
                      </span>
                      {t.unread_admin_count > 0 && (
                        <span className="sa-unread-dot">{t.unread_admin_count}</span>
                      )}
                    </div>
                    <div className="sa-ticket-meta">
                      <span
                        className="sa-status-pill"
                        style={{ color: cfg.color, background: cfg.bg }}
                      >
                        {cfg.label}
                      </span>
                    </div>
                    <div className="sa-ticket-date">{new Date(t.updated_at).toLocaleString()}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Chat Panel ────────────────────────────────────────────────── */}
        <div className="sa-chat-panel">
          {!selectedTicket ? (
            <div className="sa-chat-empty">
              <i className="fas fa-headset" />
              <h3>Select a ticket</h3>
              <p>Choose a ticket from the list to view the conversation.</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="sa-chat-header">
                <div className="sa-chat-title">
                  <button className="sa-back-btn" onClick={() => setSelectedTicket(null)}>
                    <i className="fas fa-arrow-left" />
                  </button>
                  <div>
                    <h3>
                      <span style={{ opacity: 0.5, marginRight: '10px' }}>
                        #{selectedTicket.id}
                      </span>
                      {selectedTicket.subject}
                    </h3>
                  </div>
                </div>
                <div className="sa-status-actions">
                  {statusKeys
                    .filter((s) => s !== selectedTicket.status)
                    .map((s) => (
                      <button
                        key={s}
                        className="sa-status-btn"
                        style={{
                          color: STATUS_CONFIG[s].color,
                          borderColor: STATUS_CONFIG[s].color,
                        }}
                        onClick={() => updateStatus(selectedTicket.id, s)}
                      >
                        {STATUS_CONFIG[s].label}
                      </button>
                    ))}
                </div>
              </div>

              {/* Messages */}
              <div className="sa-messages">
                {messages.length === 0 && (
                  <div className="sa-no-messages">No messages yet. Say hello! 👋</div>
                )}
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`sa-message ${msg.is_admin_reply ? 'admin' : 'user'}`}
                  >
                    <div className="sa-msg-bubble">
                      {msg.image_url && (
                        <div className="sa-msg-image">
                          <img
                            src={msg.image_url}
                            alt="Support Attachment"
                            onClick={() => window.open(msg.image_url, '_blank')}
                            style={{ cursor: 'pointer' }}
                          />
                        </div>
                      )}
                      {msg.message_text && <p>{msg.message_text}</p>}
                    </div>
                    <div className="sa-msg-meta">
                      <span className="sa-msg-time">
                        {new Date(msg.created_at).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  </div>
                ))}
                {otherTyping && (
                  <div className="sa-typing-indicator">
                    <span /> <span /> <span />
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              {selectedTicket.status === 'closed' || selectedTicket.status === 'resolved' ? (
                <div className="sa-closed-notice">
                  <i className="fas fa-lock" />
                  {selectedTicket.status === 'resolved'
                    ? ' This ticket is resolved.'
                    : ' This ticket is closed.'}
                </div>
              ) : (
                <div className="sa-input-row">
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageSelect}
                  />
                  <button
                    className="sa-upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={sending}
                    title="Upload image"
                  >
                    <i className="fas fa-image" />
                  </button>
                  <textarea
                    className="sa-input"
                    rows={1}
                    placeholder="Type your reply…"
                    value={newMessage}
                    onChange={handleTyping}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                  />
                  <button
                    className="sa-send-btn"
                    onClick={() => sendMessage()}
                    disabled={sending || !newMessage.trim()}
                  >
                    {sending ? (
                      <i className="fas fa-circle-notch fa-spin" />
                    ) : (
                      <i className="fas fa-paper-plane" />
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
